import * as React from 'react';
import { Arrow, Check, Info } from 'rcd-cpp-ui/ui/icon';
import TooltipPopover from '../../Popover/popover';
import { WorkflowStep } from '../../../types/WorkflowStep';
import { WorkflowInfo } from '../../../types/WorkflowInfo';
import { WorkflowStageUiConfigById } from '../../../types/ConfigureWorkflowTypes';
import { WorkflowStepGroup } from '../../../enums/WorkflowStepGroup';
import { ItemStatus } from '../../../enums/ItemStatus';

type TokenCounterProps = {
  count: number;
  maxCount: number;
  plannedCount: number;
  loading: boolean;
};

const TokenCounter = ({
  count,
  maxCount,
  plannedCount,
  loading
}: TokenCounterProps) => (
  <div className="token-counter">
    {loading ? (
      <div className="token-count loading">Loading...</div>
    ) : (
      <>
        <div className={`token-count ${maxCount < count ? 'error' : ''}`}>
          With Studio: {count}/{maxCount}
        </div>
        <div className="token-count info">Planned: {plannedCount}</div>
      </>
    )}
  </div>
);

type ConfigureWorkflowTreeProps = {
  workflowSteps: WorkflowStep[];
  collapsed: { [key: string]: boolean };
  setCollapsed: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
  configureWorkflowSkipIds: number[];
  onWorkflowStageChange: (statusId: number) => void;
  isLoading: boolean;
  isWorkflowDisabled: boolean;
  workflowInfo: WorkflowInfo;
  isNewLightUpdate?: boolean;
  editorialTokenCount: number;
  plannedEditorialTokenCount: number;
  creativeHelpDisabled: boolean;
  canConfigureCreativeHelp: boolean;
  isModalDisabled: boolean;
  requestCreativeHelp: boolean;
  onRequestCreativeHelpChange: (checked: boolean) => void;
  creativeTokenCount: number;
  plannedCreativeTokenCount: number;
  statusUiConfigById: WorkflowStageUiConfigById;
  requestCreativeHelpFieldName: string;
  itemStatusId?: number;
  preProductionExternalReview?: boolean;
  showPreProductionExternalReview?: boolean;
  isPreProductionExternalReviewAutoSelect?: boolean;
  postProductionExternalReview?: boolean;
  showPostProductionExternalReview?: boolean;
  isPostProductionExternalReviewAutoSelect?: boolean;
  postProductionExternalReviewFieldName?: string;
  preProductionExternalReviewFieldName?: string;
  onPreProductionExternalReviewChange?: (checked: boolean) => void;
  onPostProductionExternalReviewChange?: (checked: boolean) => void;
  canConfigureExternalReview?: boolean;
};

export const ConfigureWorkflowTree = ({
  workflowSteps,
  collapsed,
  setCollapsed,
  configureWorkflowSkipIds,
  onWorkflowStageChange,
  isLoading,
  isWorkflowDisabled,
  workflowInfo,
  isNewLightUpdate = false,
  editorialTokenCount,
  plannedEditorialTokenCount,
  creativeHelpDisabled,
  canConfigureCreativeHelp,
  isModalDisabled,
  requestCreativeHelp,
  onRequestCreativeHelpChange,
  creativeTokenCount,
  plannedCreativeTokenCount,
  statusUiConfigById,
  requestCreativeHelpFieldName,
  preProductionExternalReview,
  onPreProductionExternalReviewChange,
  showPreProductionExternalReview = false,
  isPreProductionExternalReviewAutoSelect = false,
  postProductionExternalReview,
  onPostProductionExternalReviewChange,
  showPostProductionExternalReview = false,
  isPostProductionExternalReviewAutoSelect = false,
  itemStatusId,
  preProductionExternalReviewFieldName,
  postProductionExternalReviewFieldName,
  canConfigureExternalReview = false
}: ConfigureWorkflowTreeProps) => {
  const canToggleRequestCreativeHelp = React.useMemo(
    () =>
      workflowInfo &&
      !creativeHelpDisabled &&
      canConfigureCreativeHelp &&
      !isModalDisabled,
    [
      workflowInfo,
      creativeHelpDisabled,
      canConfigureCreativeHelp,
      isModalDisabled
    ]
  );

  const canTogglePreProductionExternalReview = React.useMemo(
    () =>
      showPreProductionExternalReview &&
      !isPreProductionExternalReviewAutoSelect &&
      !isLoading &&
      canConfigureExternalReview &&
      !isModalDisabled &&
      !!workflowInfo?.isWipItem &&
      !(itemStatusId >= ItemStatus.ExternalReview),
    [
      showPreProductionExternalReview,
      isPreProductionExternalReviewAutoSelect,
      isLoading,
      canConfigureExternalReview,
      isModalDisabled,
      workflowInfo,
      itemStatusId
    ]
  );

  const canTogglePostProductionExternalReview = React.useMemo(
    () =>
      showPostProductionExternalReview &&
      !isPostProductionExternalReviewAutoSelect &&
      !isLoading &&
      canConfigureExternalReview &&
      !isModalDisabled &&
      !!workflowInfo?.isWipItem &&
      !(itemStatusId > ItemStatus.PreFlightReview),
    [
      showPostProductionExternalReview,
      isPostProductionExternalReviewAutoSelect,
      isLoading,
      canConfigureExternalReview,
      isModalDisabled,
      workflowInfo,
      itemStatusId
    ]
  );

  return (
    <div className="workflow-tree scrollable">
      {workflowSteps?.map(ws => (
        <div key={ws.groupId} className="workflow-category">
          <div
            onClick={() => {
              setCollapsed(prev => ({
                ...prev,
                [ws.group]: !prev[ws.group]
              }));
            }}
          >
            <Arrow
              type="solid"
              direction={collapsed[ws.group] ? 'right' : 'down'}
            />
            <Check />
            <span>{ws.group}</span>
          </div>
          <div className="workflow-stages">
            {collapsed[ws.group] ? null : (
              <>
                {ws.statuses?.map(
                  ({ statusRefId, statusId, status, disabled }) => {
                    const statusUiConfig = statusUiConfigById?.[statusId];
                    return (
                      <div key={statusRefId} className="workflow-stage">
                        <input
                          type="checkbox"
                          id={statusRefId}
                          checked={
                            !configureWorkflowSkipIds?.includes(statusId)
                          }
                          disabled={
                            disabled ||
                            isLoading ||
                            isWorkflowDisabled ||
                            (!workflowInfo?.isWipItem && !isNewLightUpdate)
                          }
                          onChange={() => onWorkflowStageChange(statusId)}
                        />
                        <label htmlFor={statusRefId}>{status}</label>

                        {!!statusUiConfig?.tooltipContent && (
                          <div>
                            <TooltipPopover
                              packed={true}
                              type="info"
                              pointerPosition="top"
                              content={statusUiConfig.tooltipContent}
                            >
                              <div className="info-icon">
                                <Info />
                              </div>
                            </TooltipPopover>
                          </div>
                        )}

                        {!!statusUiConfig?.showEditorialTokenCounter &&
                          (!!workflowInfo?.isWipItem || isNewLightUpdate) && (
                            <TokenCounter
                              count={editorialTokenCount}
                              maxCount={workflowInfo.maxEditorialWorkflowCount}
                              plannedCount={plannedEditorialTokenCount}
                              loading={editorialTokenCount === null}
                            />
                          )}
                      </div>
                    );
                  }
                )}
                {showPreProductionExternalReview &&
                  ws.groupId === WorkflowStepGroup.Authoring && (
                    <div className="workflow-stage">
                      <input
                        type="checkbox"
                        id="preProductionExternalReview"
                        disabled={!canTogglePreProductionExternalReview}
                        checked={preProductionExternalReview}
                        onChange={e =>
                          onPreProductionExternalReviewChange?.(
                            e.target.checked
                          )
                        }
                      />
                      <label htmlFor="preProductionExternalReview">
                        {preProductionExternalReviewFieldName}
                      </label>
                    </div>
                  )}
                {showPostProductionExternalReview &&
                  ws.groupId === WorkflowStepGroup.Authoring && (
                    <div className="workflow-stage">
                      <input
                        type="checkbox"
                        id="postProductionExternalReview"
                        disabled={!canTogglePostProductionExternalReview}
                        checked={postProductionExternalReview}
                        onChange={e =>
                          onPostProductionExternalReviewChange?.(
                            e.target.checked
                          )
                        }
                      />
                      <label htmlFor="postProductionExternalReview">
                        {postProductionExternalReviewFieldName}
                      </label>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      ))}
      {!!workflowSteps && (
        <div className="workflow-category">
          <div
            onClick={() => {
              setCollapsed(prev => ({
                ...prev,
                creative: !prev.creative
              }));
            }}
          >
            <Arrow
              type="solid"
              direction={collapsed.creative ? 'right' : 'down'}
            />
            <Check />
            <span>Design Production</span>
          </div>
          <div className="workflow-stages">
            {!collapsed.creative && (
              <div className="workflow-stage">
                <input
                  type="checkbox"
                  id="requestCreativeHelp"
                  disabled={!canToggleRequestCreativeHelp}
                  checked={requestCreativeHelp}
                  onChange={e => onRequestCreativeHelpChange(e.target.checked)}
                />
                <label
                  htmlFor="requestCreativeHelp"
                  className="requestCreativeHelp"
                >
                  {requestCreativeHelpFieldName}
                </label>
                {(!!workflowInfo?.isWipItem || isNewLightUpdate) && (
                  <TokenCounter
                    count={creativeTokenCount}
                    maxCount={workflowInfo.maxCreativeWorkflowCount}
                    plannedCount={plannedCreativeTokenCount}
                    loading={creativeTokenCount === null}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
